/**
 * Custom hook for managing PDF snippet capture
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useCallback, useRef } from 'react';
import { eventBus, EventNames } from '../../../utils/eventBus';
import logger from '../../../utils/simpleLogger';
import type { PDFCoords } from '../types/pdf-viewer.types';

// API Base URL
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

export interface UsePDFCaptureProps {
  containerRef: React.RefObject<HTMLDivElement>;
  pageRefs: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
  documentId?: string;
  documentName: string;
  documentPath: string;
  pageNumber: number;
  scale: number;
  projectId?: string;
  onSnippetSaved?: (snippetId: string) => void;
}

export interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface UsePDFCaptureReturn {
  captureMode: boolean;
  isDrawing: boolean;
  captureRect: CaptureRect | null;
  startPoint: { x: number; y: number } | null;
  capturedImage: string | null;
  showSaveModal: boolean;
  setCaptureMode: (mode: boolean) => void;
  setIsDrawing: (drawing: boolean) => void;
  setCaptureRect: (rect: CaptureRect | null) => void;
  setStartPoint: (point: { x: number; y: number } | null) => void;
  setCapturedImage: (image: string | null) => void;
  setShowSaveModal: (show: boolean) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => Promise<void>;
  handleAutoSaveSnippet: (imageData?: string | null) => Promise<string | null>;
  handleSaveSnippet: (data: { title: string; notes: string; tags: string[] }) => Promise<void>;
  convertContainerCoordsToPDFCoords: (containerRect: CaptureRect) => PDFCoords | null;
}

/**
 * Manages PDF snippet capture state and operations
 */
export function usePDFCapture({
  containerRef,
  pageRefs,
  documentId,
  documentName,
  documentPath,
  pageNumber,
  scale,
  projectId,
  onSnippetSaved,
}: UsePDFCaptureProps): UsePDFCaptureReturn {
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [captureRect, setCaptureRect] = useState<CaptureRect | null>(null);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  // Helper function to get document ID
  const getDocumentId = useCallback(async (): Promise<string | null> => {
    if (documentId) return documentId;

    if (!documentName) return null;

    logger.info('🔍 Intentando obtener document_id del nombre del documento...', {
      documentName,
      documentPath,
    });
    try {
      const apiModule = await import('../../../services/api');
      const apiService: any = apiModule.default || apiModule.apiService;

      // Buscar el documento por nombre en documentos privados y compartidos
      const [documentsResponse, sharedDocumentsResponse] = await Promise.all([
        apiService.getDocuments().catch(() => ({ documents: [] })),
        apiService.getSharedDocuments?.().catch(() => ({ documents: [] })) ||
          Promise.resolve({ documents: [] }),
      ]);

      const privateDocuments = documentsResponse?.documents || [];
      const sharedDocuments = sharedDocumentsResponse?.documents || [];
      const documents = [...privateDocuments, ...sharedDocuments];

      logger.info(
        `📋 Total documentos: ${documents.length} (${privateDocuments.length} privados, ${sharedDocuments.length} compartidos)`
      );

      // Normalizar nombres para comparación
      const normalizeName = (name: string) => {
        if (!name) return '';
        return name
          .toLowerCase()
          .replace(/\.[^/.]+$/, '')
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '')
          .trim();
      };

      const normalizedDocumentName = normalizeName(documentName);

      // Buscar por múltiples criterios
      const matchingDoc = documents.find((doc: any) => {
        if (doc.name === documentName || doc.original_filename === documentName) return true;

        const normalizedDocName = normalizeName(doc.name || '');
        const normalizedOriginalName = normalizeName(doc.original_filename || '');

        if (
          normalizedDocName === normalizedDocumentName ||
          normalizedOriginalName === normalizedDocumentName
        )
          return true;

        if (
          normalizedDocumentName &&
          (normalizedDocName.includes(normalizedDocumentName) ||
            normalizedDocumentName.includes(normalizedDocName) ||
            normalizedOriginalName.includes(normalizedDocumentName) ||
            normalizedDocumentName.includes(normalizedOriginalName))
        )
          return true;

        if (documentPath && doc.original_filename) {
          const normalizedPath = normalizeName(documentPath);
          const normalizedDocFilename = normalizeName(doc.original_filename);
          if (
            normalizedPath.includes(normalizedDocFilename) ||
            normalizedDocFilename.includes(normalizedPath)
          )
            return true;
        }

        return false;
      });

      if (matchingDoc) {
        logger.info(`✅ Document ID encontrado: ${matchingDoc.id}`);
        return matchingDoc.id;
      }
    } catch (err) {
      console.error('❌ Error al buscar documento:', err);
    }

    return null;
  }, [documentId, documentName, documentPath]);

  // Convertir coordenadas del mouse/contenedor a coordenadas PDF reales
  const convertContainerCoordsToPDFCoords = useCallback(
    (containerRect: CaptureRect): PDFCoords | null => {
      if (!containerRef.current) {
        console.error('❌ No hay referencia al contenedor');
        return null;
      }

      // Obtener las dimensiones del PDF renderizado
      const pdfPage = document.querySelector('.react-pdf__Page') as HTMLElement;
      if (!pdfPage) {
        console.error('❌ No se encontró la página PDF renderizada');
        return null;
      }

      const pdfPageRect = pdfPage.getBoundingClientRect();
      const containerBounds = containerRef.current.getBoundingClientRect();

      // Calcular offset del PDF dentro del contenedor
      const pdfOffsetX = pdfPageRect.left - containerBounds.left;
      const pdfOffsetY = pdfPageRect.top - containerBounds.top;

      // Ajustar coordenadas relativas al PDF (no al contenedor)
      const pdfRelativeX = containerRect.x - pdfOffsetX;
      const pdfRelativeY = containerRect.y - pdfOffsetY;

      // Dimensiones del PDF renderizado (con zoom aplicado)
      const renderedWidth = pdfPageRect.width;
      const renderedHeight = pdfPageRect.height;

      // Calcular coordenadas como porcentajes del PDF renderizado
      const relativeX = (pdfRelativeX / renderedWidth) * 100;
      const relativeY = (pdfRelativeY / renderedHeight) * 100;
      const relativeWidth = (containerRect.width / renderedWidth) * 100;
      const relativeHeight = (containerRect.height / renderedHeight) * 100;

      // INVERTIR EJE Y: Frontend (top-left) → PDF (bottom-left)
      const invertedY = 100 - relativeY - relativeHeight;

      let x = relativeX;
      let y = invertedY;
      let width = relativeWidth;
      let height = relativeHeight;

      if (x < 0) {
        width += x;
        x = 0;
      }
      if (y < 0) {
        height += y;
        y = 0;
      }

      if (x + width > 100) {
        width = 100 - x;
      }
      if (y + height > 100) {
        height = 100 - y;
      }

      const clamp = (value: number) => Math.max(0, Math.min(100, value));

      x = clamp(x);
      y = clamp(y);
      width = clamp(width);
      height = clamp(height);

      if (width <= 0 || height <= 0) {
        console.warn('❌ Selección fuera de rango. Ignorando.');
        return null;
      }

      logger.debug?.('🎯 Coordenadas convertidas:', { x, y, width, height });

      return { x, y, width, height };
    },
    [containerRef]
  );

  // Capturar usando el backend
  const captureScreenshotViaAPI = useCallback(
    async (relativeCoords: PDFCoords): Promise<string | null> => {
      if (!containerRef.current) return null;
      if (!documentId) {
        console.warn('❌ No documentId available, skipping backend capture fallback to canvas');
        return null;
      }

      try {
        logger.info('🎯 Capturando imagen via API backend...');
        logger.info('   📐 Coordenadas relativas:', relativeCoords);
        logger.info('   📐 Zoom actual:', scale);
        logger.info('   📄 Página:', pageNumber);
        logger.info('   📄 Documento ID:', documentId);

        const token = localStorage.getItem('access_token');

        const requestBody = {
          page: pageNumber,
          relative_coordinates: {
            x: relativeCoords.x,
            y: relativeCoords.y,
            width: relativeCoords.width,
            height: relativeCoords.height,
          },
          dpi: 200,
        };

        logger.info('🌐 Enviando coordenadas relativas:', requestBody);

        const response = await fetch(`${API_BASE}/pdf-crop/${documentId}/crop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
        }

        const blob = await response.blob();
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            logger.info('✅ API crop exitoso con coordenadas relativas');
            resolve(reader.result as string);
          };
          reader.onerror = (error) => {
            console.error('❌ Error leyendo blob:', error);
            reject(error);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('❌ Error capturando via API:', error);
        return null;
      }
    },
    [containerRef, documentId, pageNumber, scale]
  );

  // Capturar usando canvas local
  const captureScreenshotViaCanvas = useCallback(
    async (relativeCoords: PDFCoords): Promise<string | null> => {
      const pageElement =
        pageRefs.current[pageNumber] ??
        (document.querySelector('.react-pdf__Page') as HTMLElement | null);
      if (!pageElement) {
        console.error('❌ No se encontró la página PDF para captura local');
        return null;
      }

      const canvasElement = pageElement.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvasElement) {
        console.error('❌ No se encontró el canvas del PDF para captura local');
        return null;
      }

      const canvasWidth = canvasElement.width;
      const canvasHeight = canvasElement.height;

      if (canvasWidth === 0 || canvasHeight === 0) {
        console.error('❌ Canvas inválido para captura local');
        return null;
      }

      // Convertir porcentajes (sistema PDF) a píxeles (canvas)
      const cropWidth = Math.max(1, Math.round((relativeCoords.width / 100) * canvasWidth));
      const cropHeight = Math.max(1, Math.round((relativeCoords.height / 100) * canvasHeight));

      const cropX = Math.max(0, Math.round((relativeCoords.x / 100) * canvasWidth));
      const cropY = Math.max(
        0,
        Math.round(((100 - relativeCoords.y - relativeCoords.height) / 100) * canvasHeight)
      );

      const temporaryCanvas = document.createElement('canvas');
      temporaryCanvas.width = cropWidth;
      temporaryCanvas.height = cropHeight;

      const tempCtx = temporaryCanvas.getContext('2d');
      if (!tempCtx) {
        console.error('❌ No se pudo crear contexto para captura local');
        return null;
      }

      tempCtx.drawImage(
        canvasElement,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      try {
        const dataUrl = temporaryCanvas.toDataURL('image/png');
        logger.info('✅ Screenshot capturado localmente');
        return dataUrl;
      } catch (error) {
        console.error('❌ Error generando imagen local:', error);
        return null;
      }
    },
    [pageRefs, pageNumber]
  );

  // Capturar screenshot
  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    if (!captureRect || !containerRef.current) return null;

    logger.info('📸 Iniciando captura de snippet...');

    // Convertir coordenadas primero
    const relativeCoords = convertContainerCoordsToPDFCoords(captureRect);
    if (!relativeCoords) {
      console.error('❌ No se pudieron obtener coordenadas relativas para el snippet');
      return null;
    }

    // Intentar captura via API si es posible
    const apiResult = await captureScreenshotViaAPI(relativeCoords);
    if (apiResult) {
      logger.info('✅ Screenshot capturado via API:', apiResult.substring(0, 50) + '...');
      return apiResult;
    }

    // Fallback: captura local desde canvas renderizado
    const canvasResult = await captureScreenshotViaCanvas(relativeCoords);
    if (canvasResult) {
      logger.info('✅ Screenshot capturado via canvas:', canvasResult.substring(0, 50) + '...');
      return canvasResult;
    }

    // Si ambas fallan, mostrar error claro
    console.error('❌ Error: No se pudo capturar el fragmento con ninguna estrategia');
    alert('Error: No se pudo capturar el fragmento. Por favor, intenta nuevamente.');
    return null;
  }, [
    captureRect,
    containerRef,
    convertContainerCoordsToPDFCoords,
    captureScreenshotViaAPI,
    captureScreenshotViaCanvas,
  ]);

  // Auto-save snippet (without modal)
  const handleAutoSaveSnippet = useCallback(
    async (imageData?: string | null): Promise<string | null> => {
      const imageToSave = imageData ?? capturedImage;
      if (!captureRect || !imageToSave) {
        console.error('❌ No hay captura para guardar', {
          captureRect: !!captureRect,
          imageData: !!imageData,
          capturedImage: !!capturedImage,
        });
        return null;
      }

      const finalDocumentId = await getDocumentId();
      if (!finalDocumentId) {
        console.error('❌ No se pudo obtener document_id');
        return null;
      }

      try {
        const relativeCoords = convertContainerCoordsToPDFCoords(captureRect);
        if (!relativeCoords) {
          console.error('❌ No se pudieron convertir las coordenadas');
          return null;
        }

        logger.info('📤 Guardando snippet automáticamente...');

        const apiModule = await import('../../../services/api');
        const apiService: any = apiModule.default || apiModule.apiService;

        const tempTitle = `${documentName} - Pág. ${pageNumber}`;

        const snippet = await apiService.createPDFSnippet({
          document_id: finalDocumentId,
          title: tempTitle,
          notes: '',
          tags: [],
          page_number: pageNumber,
          coordinates: {
            x: relativeCoords.x,
            y: relativeCoords.y,
            width: relativeCoords.width,
            height: relativeCoords.height,
            scale: 1,
            is_relative: true,
          },
          snippet_type: 'image',
          image_data: imageToSave,
          auto_process: true,
        });

        logger.info('✅ Snippet guardado automáticamente:', snippet.id);

        // Limpiar estado
        setCapturedImage(null);
        setCaptureRect(null);
        setCaptureMode(false);
        setStartPoint(null);

        // Emitir evento
        eventBus.emit(EventNames.SAVED_ITEM_CREATED, {
          type: 'snippet',
          snippetId: snippet.id,
          documentId: finalDocumentId,
        });

        // Llamar callback si existe
        if (onSnippetSaved) {
          onSnippetSaved(snippet.id);
        }

        return snippet.id;
      } catch (error: any) {
        console.error('❌ Error guardando snippet automáticamente:', error);
        return null;
      }
    },
    [
      captureRect,
      capturedImage,
      getDocumentId,
      documentName,
      pageNumber,
      convertContainerCoordsToPDFCoords,
      onSnippetSaved,
    ]
  );

  // Handle save snippet (manual save from modal)
  const handleSaveSnippet = useCallback(
    async (data: { title: string; notes: string; tags: string[] }): Promise<void> => {
      logger.info('💾 Guardando snippet:', data);

      const finalDocumentId = await getDocumentId();

      if (!finalDocumentId) {
        console.error('❌ No se pudo obtener document_id', { documentId, documentName });
        alert(
          'Error: No se puede guardar el fragmento.\n\n' +
            'No se pudo identificar el documento. Por favor:\n' +
            '• Asegúrate de que el documento esté cargado\n' +
            '• Intenta seleccionar el documento desde el chat o el Drive\n' +
            '• Verifica que el documento tenga un nombre válido'
        );
        return;
      }

      if (!captureRect || !capturedImage) {
        console.error('❌ No hay captura para guardar');
        return;
      }

      try {
        const relativeCoords = convertContainerCoordsToPDFCoords(captureRect);
        if (!relativeCoords) {
          console.error('❌ No se pudieron convertir las coordenadas para guardar');
          alert('Error: No se pudieron procesar las coordenadas del fragmento.');
          return;
        }

        logger.info('📤 Guardando snippet con coordenadas relativas...');
        logger.info('   Document ID:', finalDocumentId);
        logger.info('   Página:', pageNumber);
        logger.info('   Coordenadas relativas:', relativeCoords);

        const apiModule = await import('../../../services/api');
        const apiService: any = apiModule.default || apiModule.apiService;

        const snippet = await apiService.createPDFSnippet({
          document_id: finalDocumentId,
          title: data.title,
          notes: data.notes,
          tags: data.tags,
          page_number: pageNumber,
          coordinates: {
            x: relativeCoords.x,
            y: relativeCoords.y,
            width: relativeCoords.width,
            height: relativeCoords.height,
            scale: 1,
            is_relative: true,
          },
          snippet_type: 'image',
          image_data: capturedImage,
        });

        logger.info('✅ Snippet guardado exitosamente:', snippet.id);

        // Cerrar modal y limpiar
        setShowSaveModal(false);
        setCapturedImage(null);
        setCaptureRect(null);

        // Emitir evento
        eventBus.emit(EventNames.SAVED_ITEM_CREATED, {
          type: 'snippet',
          snippetId: snippet.id,
          projectId: projectId,
          documentId: documentId,
        });

        alert('✅ Fragmento guardado exitosamente');
      } catch (error: any) {
        console.error('❌ Error guardando snippet:', error);
        alert(`Error al guardar: ${error.message || 'Error desconocido'}`);
      }
    },
    [
      captureRect,
      capturedImage,
      getDocumentId,
      documentName,
      documentId,
      pageNumber,
      projectId,
      convertContainerCoordsToPDFCoords,
    ]
  );

  // Capture mode handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!captureMode) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setStartPoint({ x, y });
      setIsDrawing(true);
      setCaptureRect(null);
    },
    [captureMode, containerRef]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!captureMode || !isDrawing || !startPoint) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      const width = currentX - startPoint.x;
      const height = currentY - startPoint.y;

      // Usar requestAnimationFrame para suavizar el rendering
      requestAnimationFrame(() => {
        setCaptureRect({
          x: width > 0 ? startPoint.x : currentX,
          y: height > 0 ? startPoint.y : currentY,
          width: Math.abs(width),
          height: Math.abs(height),
        });
      });
    },
    [captureMode, isDrawing, startPoint, containerRef]
  );

  const handleMouseUp = useCallback(async () => {
    if (!captureMode || !isDrawing) return;

    setIsDrawing(false);

    // Si hay un rectángulo válido, capturar y guardar automáticamente
    if (captureRect && captureRect.width > 10 && captureRect.height > 10) {
      logger.info('📸 Captura completada:', captureRect);

      // Capturar screenshot
      const screenshot = await captureScreenshot();
      if (screenshot) {
        setCapturedImage(screenshot);
        // Guardar automáticamente sin mostrar modal, pasando el screenshot directamente
        await handleAutoSaveSnippet(screenshot);
      } else {
        // Si falla la captura, limpiar todo
        setCaptureMode(false);
        setCaptureRect(null);
        setStartPoint(null);
      }
    } else {
      // Rectángulo muy pequeño, resetear
      setCaptureRect(null);
      setStartPoint(null);
    }
  }, [captureMode, isDrawing, captureRect, captureScreenshot, handleAutoSaveSnippet]);

  return {
    captureMode,
    isDrawing,
    captureRect,
    startPoint,
    capturedImage,
    showSaveModal,
    setCaptureMode,
    setIsDrawing,
    setCaptureRect,
    setStartPoint,
    setCapturedImage,
    setShowSaveModal,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleAutoSaveSnippet,
    handleSaveSnippet,
    convertContainerCoordsToPDFCoords,
  };
}
